import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../../utils/api';
import { FaSearch, FaFilter, FaDownload } from 'react-icons/fa';
import './InquiriesDashboard.css';

const InquiriesDashboard = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    insuranceType: '',
    city: '',
    startDate: '',
    endDate: '',
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });

  useEffect(() => {
    fetchInquiries();
  }, [filters]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.insuranceType) params.append('insuranceType', filters.insuranceType);
      if (filters.city) params.append('city', filters.city);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      params.append('sortBy', filters.sortBy);
      params.append('sortOrder', filters.sortOrder);

      const response = await axios.get(`/inquiries/all?${params.toString()}`);
      setInquiries(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const handleExport = async () => {
    try {
      const response = await axios.get('/inquiries/export', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inquiries-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Inquiries exported successfully');
    } catch (error) {
      toast.error('Failed to export inquiries');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      insuranceType: '',
      city: '',
      startDate: '',
      endDate: '',
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
  };

  return (
    <div className="inquiries-dashboard">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Inquiries Dashboard</h1>
            <p>Manage and track all customer inquiries</p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="container">
          <div className="filters-section card">
            <div className="filters-header">
              <FaFilter /> Filters
            </div>
            <div className="filters-grid">
              <div className="filter-group">
                <label>Search</label>
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    name="search"
                    placeholder="Search by name, email, or mobile..."
                    value={filters.search}
                    onChange={handleFilterChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Insurance Type</label>
                <select
                  name="insuranceType"
                  value={filters.insuranceType}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">All Types</option>
                  <option value="Car Insurance">Car Insurance</option>
                  <option value="Bike Insurance">Bike Insurance</option>
                  <option value="Health Insurance">Health Insurance</option>
                  <option value="Commercial Insurance">Commercial Insurance</option>
                  <option value="Travel Insurance">Travel Insurance</option>
                </select>
              </div>

              <div className="filter-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  placeholder="Filter by city..."
                  value={filters.city}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="form-input"
                />
              </div>

              <div className="filter-group">
                <label>Sort By</label>
                <select
                  name="sortBy"
                  value={filters.sortBy}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="timestamp">Date</option>
                  <option value="fullName">Name</option>
                  <option value="insuranceType">Insurance Type</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Order</label>
                <select
                  name="sortOrder"
                  value={filters.sortOrder}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              <div className="filter-group">
                <button onClick={clearFilters} className="btn btn-secondary">
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          <div className="summary-stats">
            <div className="stat-badge">
              Total: <strong>{inquiries.length}</strong> inquiries
            </div>
            <button onClick={handleExport} className="btn btn-primary">
              <FaDownload /> Export CSV
            </button>
          </div>

          {loading ? (
            <div className="spinner"></div>
          ) : inquiries.length === 0 ? (
            <div className="empty-state card">
              <p>No inquiries found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="inquiries-table-wrapper card">
              <table className="inquiries-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Email</th>
                    <th>City</th>
                    <th>Insurance Type</th>
                    <th>Vehicle Type</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.map((inquiry) => (
                    <tr key={inquiry._id}>
                      <td>{inquiry.fullName}</td>
                      <td>{inquiry.mobile}</td>
                      <td>{inquiry.email}</td>
                      <td>{inquiry.city}</td>
                      <td>
                        <span className="badge">{inquiry.insuranceType}</span>
                      </td>
                      <td>{inquiry.vehicleType || '-'}</td>
                      <td>{formatDate(inquiry.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InquiriesDashboard;


